import React, { useCallback } from 'react'

import { XStack } from 'tamagui'

import { Button } from '@newComponents/Button/Button'
import { Spinner } from '@newComponents/Spinner'
import { Text } from '@newComponents/Text'
import { Icon } from '@newComponents/Icon'
import { Link } from '@newComponents/Link'

import { useSettingsEditExternal } from './SettingsEditProvider'

// --- Module-level callback for save-and-navigate ---

let _onSaveAndNavigate: (() => Promise<void>) | null = null

export const setOnSaveAndNavigate = (fn: (() => Promise<void>) | null) => {
  _onSaveAndNavigate = fn
}

// --- Module-level callback for delete-and-navigate ---

let _onDeleteAndNavigate: ((targetUrl: string) => void) | null = null

export const setOnDeleteAndNavigate = (
  fn: ((targetUrl: string) => void) | null
) => {
  _onDeleteAndNavigate = fn
}

export const triggerDeleteAndNavigate = (targetUrl: string) => {
  if (_onDeleteAndNavigate) _onDeleteAndNavigate(targetUrl)
}

// --- Back Link ---

export const SettingsBackLink = ({
  href,
  label,
}: {
  href: string
  label: string
}) => {
  return (
    <Link
      href={href}
      hoverStyle={{ textDecorationLine: 'none' }}
      paddingLeft='$1'
      paddingRight='$2'
    >
      <XStack alignItems='center' gap='$2'>
        <Icon name='arrow-left' size='$4' />
        <Text fontWeight='$6' size='$4'>
          {label}
        </Text>
      </XStack>
    </Link>
  )
}

// --- Save Button ---

export const SettingsSaveButton = ({
  settingsId,
}: {
  settingsId: string
}) => {
  const { hasPendingChanges, isSaving } = useSettingsEditExternal(settingsId)

  const disabled = !hasPendingChanges || isSaving

  const handleSave = useCallback(async () => {
    if (_onSaveAndNavigate) {
      await _onSaveAndNavigate()
    }
  }, [])

  return (
    <Button
      onPress={handleSave}
      disabled={disabled}
      opacity={disabled ? 0.6 : 1}
      backgroundColor={hasPendingChanges ? '$darkBlue9' : '$grey4'}
      paddingHorizontal='$4'
      paddingVertical='$2'
      minHeight={36}
      successFlash={false}
      errorFlash={false}
      small
      borderRadius='$10'
    >
      <XStack alignItems='center' gap='$2'>
        {isSaving ? (
          <>
            <Spinner size='$3' color='$grey0' />
            <Button.Text size='$4'>Saving...</Button.Text>
          </>
        ) : (
          <Button.Text size='$4'>Save</Button.Text>
        )}
      </XStack>
    </Button>
  )
}

// --- Status Text ---

export const SettingsStatusText = ({
  settingsId,
}: {
  settingsId: string
}) => {
  const { hasPendingChanges, isSaving, pendingCount } =
    useSettingsEditExternal(settingsId)

  if (isSaving) {
    return (
      <Text fontSize='$4' color='$grey8'>
        Saving changes...
      </Text>
    )
  }

  if (hasPendingChanges) {
    return (
      <Text fontSize='$4' color='$orange9'>
        {pendingCount} unsaved change{pendingCount !== 1 ? 's' : ''}
      </Text>
    )
  }

  return (
    <Text fontSize='$4' color='$grey6'>
      No changes
    </Text>
  )
}
